import { useMemo } from "react";
import RBPagination from "react-bootstrap/Pagination";
import Form from "react-bootstrap/Form";
import styles from "../Pagination.module.scss";

const DEFAULT_PAGE_SIZES = [5, 10, 20, 50, 100];

export default function ServerPagination({
  page = 1,
  pageSize = 10,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
}) {
  const safePageSize =
    Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 10;
  const safePage = Number.isFinite(page) && page > 0 ? page : 1;

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / safePageSize));
  }, [totalCount, safePageSize]);

  const currentPage = Math.min(Math.max(safePage, 1), totalPages);

  const arrayPreviousPages = Array.from(
    { length: Math.min(currentPage - 1, 2) },
    (_, i) => currentPage - i - 1
  ).reverse();

  const arrayNextPages = Array.from(
    { length: Math.min(totalPages - currentPage, 2) },
    (_, i) => currentPage + i + 1
  );

  const firstOfPreviousPages = arrayPreviousPages[0];
  const lastOfNextPages = arrayNextPages[arrayNextPages.length - 1];
  const allPagesArray = Array.from({ length: totalPages }, (_, i) => i + 1);

  const prevEllipsis =
    firstOfPreviousPages > 2
      ? Math.floor((firstOfPreviousPages - 1) / 2) + 1
      : null;

  const nextEllipsis =
    lastOfNextPages < totalPages - 1
      ? Math.floor((totalPages - lastOfNextPages) / 2) + lastOfNextPages
      : null;

  const startIndex =
    totalCount === 0 ? 0 : (currentPage - 1) * safePageSize + 1;
  const endIndex =
    totalCount === 0 ? 0 : Math.min(currentPage * safePageSize, totalCount);

  const handlePageSizeChange = (newSize) => {
    if (typeof onPageSizeChange === "function") {
      onPageSizeChange(newSize);
    }
    if (typeof onPageChange === "function" && currentPage !== 1) {
      onPageChange(1);
    }
  };

  return (
    <div className={styles.Pagination}>
      <RBPagination>
        {currentPage > 1 && firstOfPreviousPages > 1 && (
          <RBPagination.First onClick={() => onPageChange?.(1)}>
            {1}
          </RBPagination.First>
        )}
        {prevEllipsis && (
          <RBPagination.Ellipsis onClick={() => onPageChange?.(prevEllipsis)} />
        )}
        {arrayPreviousPages.map((number) => (
          <RBPagination.Item
            key={number}
            onClick={() => onPageChange?.(number)}
          >
            {number}
          </RBPagination.Item>
        ))}
        {totalPages > 1 && (
          <Form.Select
            value={currentPage}
            onChange={(e) => onPageChange?.(parseInt(e.target.value, 10))}
            className={styles.PageSelect}
          >
            {allPagesArray.map((pageNumber) => (
              <option key={pageNumber} value={pageNumber}>
                {`${pageNumber}`}
              </option>
            ))}
          </Form.Select>
        )}
        {arrayNextPages.map((number) => (
          <RBPagination.Item
            key={number}
            onClick={() => onPageChange?.(number)}
          >
            {number}
          </RBPagination.Item>
        ))}
        {nextEllipsis && (
          <RBPagination.Ellipsis onClick={() => onPageChange?.(nextEllipsis)} />
        )}
        {currentPage < totalPages && lastOfNextPages < totalPages && (
          <RBPagination.Last onClick={() => onPageChange?.(totalPages)}>
            {totalPages}
          </RBPagination.Last>
        )}
      </RBPagination>
      <span>{`${startIndex} a ${endIndex} de ${totalCount}`}</span>
      <Form.Select
        value={safePageSize}
        onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      >
        {pageSizeOptions.map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </Form.Select>
    </div>
  );
}
